import { useState } from 'react';
import { Loader2, UserPlus } from 'lucide-react';

import { api, ApiError } from '../lib/api.js';

/**
 * First-run password setup, from an invite code.
 *
 * Reached at /admin/setup with a code an existing admin generated by running
 * `npm run admin:invite`. Deliberately not an emailed link: this app sends
 * mail to customers, and an account-takeover path arriving in the same channel
 * is the thing worth not building.
 */
export function Setup({ onDone }: { onDone: () => Promise<void> }) {
  const [email, setEmail] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < 12;
  const mismatch = confirm.length > 0 && password !== confirm;
  const ready = email && inviteCode && password.length >= 12 && password === confirm;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/admin/setup', { email, inviteCode, password });
      setPassword('');
      setConfirm('');
      await onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not set that password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold tracking-tight text-gold-700">Gold Star Printing</h1>
          <p className="mt-1 text-sm text-slate-600">Set up your account</p>
        </div>

        <form onSubmit={submit} className="card space-y-4 p-6">
          <div className="flex items-center gap-2 text-slate-900">
            <UserPlus className="h-5 w-5 text-gold-600" aria-hidden />
            <h2 className="font-semibold">Choose a password</h2>
          </div>

          <div>
            <label className="field-label" htmlFor="setup-email">
              Email
            </label>
            <input
              id="setup-email"
              type="email"
              className="field-input"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div>
            <label className="field-label" htmlFor="invite">
              Invite code
            </label>
            <input
              id="invite"
              className="field-input font-account"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
              required
            />
          </div>

          <div>
            <label className="field-label" htmlFor="new-password">
              Password
            </label>
            <input
              id="new-password"
              type="password"
              className={`field-input ${tooShort ? 'field-input-error' : ''}`}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <p className={`mt-1 text-xs ${tooShort ? 'text-red-700' : 'text-slate-500'}`}>
              At least 12 characters. A short phrase you can remember beats a short word with
              symbols in it.
            </p>
          </div>

          <div>
            <label className="field-label" htmlFor="confirm-password">
              Password again
            </label>
            <input
              id="confirm-password"
              type="password"
              className={`field-input ${mismatch ? 'field-input-error' : ''}`}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
            {mismatch && <p className="field-error">These do not match.</p>}
          </div>

          {error && (
            <p className="field-error" role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="btn-primary w-full" disabled={busy || !ready}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Set password
          </button>

          <p className="text-xs text-slate-500">
            You will set up an authenticator app next. Have your phone to hand.
          </p>
        </form>
      </div>
    </div>
  );
}
